import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ConfirmModal } from '../common';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  BookOpenIcon,
  UserGroupIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import '../../styles/components.css';
import AdminDashboard from '../admin/AdminDashboard';
import { formatDate, isToday, isTomorrow } from '../../utils/dateUtils';
import { useConcessionTypes, poolDisplayName } from '../../utils/concessions';

const Dashboard = () => {
  const { user, isAdmin, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const { types: typeNames } = useConcessionTypes(!isAdmin);

  // All concession pools this user can spend from: legacy shared + each mark.
  const userPurses = (() => {
    if (!user) return [];
    const purses = [{
      key: 'shared',
      name: poolDisplayName(typeNames, 'shared'),
      balance: user.concessions || 0,
      isShared: true
    }];
    (Array.isArray(user.concessionTypes) ? user.concessionTypes : []).forEach((key) => {
      const balances = user.concessionBalances || {};
      purses.push({
        key,
        name: poolDisplayName(typeNames, key),
        balance: typeof balances[key] === 'number' ? balances[key] : 0,
        isShared: false
      });
    });
    return purses;
  })();
  const hasMarks = Array.isArray(user?.concessionTypes) && user.concessionTypes.length > 0;

  // Per-purse low indicator shown as a badge on each concession card.
  // Red at/below zero, amber when down to the last couple of credits.
  const purseAlert = (balance) => {
    if (balance <= 0) return { show: true, tone: 'red', label: 'Concessions low' };
    if (balance <= 2) return { show: true, tone: 'amber', label: 'Concessions low' };
    return { show: false };
  };
  
  const [adminBankDetails, setAdminBankDetails] = useState(null);
  const [stats, setStats] = useState({
    totalActiveBookings: 0,
    classesAttended: 0,
    availableConcessions: user?.concessions || 0,
    upcomingClasses: 0
  });
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [waitlistClasses, setWaitlistClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'waitlist'
  const [confirmWaitlist, setConfirmWaitlist] = useState({ open: false, classId: null, className: '' });
  const [confirmCancel, setConfirmCancel] = useState({ open: false, bookingId: null, className: '', startTime: null });
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  // Check if there are available spots in waitlist classes
  const hasAvailableSpotsInWaitlist = waitlistClasses.some(waitlistClass => 
    waitlistClass.availableSpots > 0 && !waitlistClass.isFull
  );

  // Fetch admin's bank account details for all users
  const fetchAdminBankDetails = async () => {
    try {
      const response = await axios.get('/api/admin/bank-details');
      
      // Check if we have valid bank account data
      if (response.data && response.data.bankAccount && 
          response.data.bankAccount.bankName && 
          response.data.bankAccount.accountNumber) {
        setAdminBankDetails(response.data.bankAccount);
      } else {
        setAdminBankDetails(null);
      }
    } catch (error) {
      console.error('Error fetching admin bank details:', error);
      setAdminBankDetails(null);
    }
  };

  // Fetch user's waitlist status
  const fetchWaitlistStatus = async () => {
    try {
      const response = await axios.get('/api/users/waitlist-status');
      setWaitlistClasses(response.data.waitlist || []);
    } catch (error) {
      console.error('Error fetching waitlist status:', error);
      setWaitlistClasses([]);
    }
  };

  // Leave waitlist from dashboard
  const leaveWaitlistFromDashboard = async (classId, className) => {
    try {
      const response = await axios.delete(`/api/bookings/waitlist/${classId}`);
      
      if (response.data.success) {
        // Show success message
        toast.success(`Successfully removed from waitlist for ${className}`);
        // Refresh waitlist data
        await fetchWaitlistStatus();
      } else {
        // Handle case where user is not on waitlist
        if (response.data.message === 'User is not on the waitlist') {
          toast.info('You are not currently on the waitlist for this class');
          // Refresh waitlist data to update UI
          await fetchWaitlistStatus();
        } else {
          toast.error(response.data.message || 'Failed to leave waitlist');
        }
      }
    } catch (error) {
      console.error('Error leaving waitlist:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to leave waitlist');
      }
    }
  };

  // Cancel booking functionality
  const cancelBooking = async (bookingId, reason = '') => {
    try {
      const response = await axios.put(`/api/bookings/cancel/${bookingId}`, { reason });
      toast.success(response.data.message || 'Booking cancelled successfully');
      // Refresh dashboard data
      await fetchDashboardData();
      // Reload the page to update concession count
      window.location.reload();
    } catch (error) {
      console.error('Cancel booking error:', error);
      const message = error.response?.data?.message || 'Failed to cancel booking';
      toast.error(message);
    }
  };

  // Handle opening class modal for waitlist booking
  const handleOpenClassModal = async (waitlistItem) => {
    try {
      // Fetch fresh class data
      const response = await axios.get(`/api/classes/${waitlistItem.classId}`);
      const freshClassData = response.data;
      
      // Use the user-specific data from the API response
      const updatedClassData = {
        ...freshClassData,
        isUserBooked: freshClassData.isUserBooked || false,
        isUserOnWaitlist: freshClassData.isUserOnWaitlist || false,
        availableSpots: freshClassData.availableSpots || (freshClassData.maxCapacity - freshClassData.currentBookings),
        isFull: freshClassData.isFull || ((freshClassData.maxCapacity - freshClassData.currentBookings) === 0)
      };
      
      setSelectedClass(updatedClassData);
      setShowClassModal(true);
    } catch (error) {
      console.error('Error fetching class data:', error);
      toast.error('Failed to load class details');
    }
  };

  // Handle closing class modal
  const handleCloseClassModal = () => {
    setShowClassModal(false);
    setSelectedClass(null);
  };

  // Fetch dashboard data function
  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch user stats
      const statsResponse = await axios.get('/api/users/stats');
      const userStats = statsResponse.data;
      
      // Update stats with user's concession count from AuthContext
      setStats({
        totalActiveBookings: userStats.bookings?.total || 0,
        classesAttended: userStats.bookings?.attended || 0,
        availableConcessions: user?.concessions || 0,
        upcomingClasses: 0 // Will be updated below
      });

      // Fetch upcoming classes
      const classesResponse = await axios.get('/api/users/upcoming-classes');
      const upcomingClassesData = classesResponse.data.bookings || [];
      setUpcomingClasses(upcomingClassesData);
      
      // Update stats with accurate counts
      setStats(prevStats => ({
        ...prevStats,
        upcomingClasses: upcomingClassesData.length,
        totalActiveBookings: upcomingClassesData.filter(booking => 
          booking.status === 'confirmed' || booking.status === 'attended'
        ).length
      }));

      // Fetch waitlist status for regular users
      if (!isAdmin) {
        await fetchWaitlistStatus();
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await fetchDashboardData();
      await fetchAdminBankDetails();
      setLoading(false);
    };

    loadDashboard();
  }, [fetchDashboardData]); // Watch user object changes

  // If user is admin, render the admin dashboard
  if (isAdmin) {
    return <AdminDashboard />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full w-12 h-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = 'blue', alert }) => (
    <div className="bg-card rounded-xl shadow-sm border border-primary p-6 hover:shadow-md transition-shadow duration-200 relative">
      {alert?.show && (
        <div
          className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            alert.tone === 'red'
              ? 'bg-red-100 text-red-700'
              : 'bg-orange-100 text-orange-700'
          }`}
        >
          <ExclamationTriangleIcon className="w-3.5 h-3.5" />
          {alert.label}
        </div>
      )}
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-secondary">{title}</p>
          <p className="text-2xl font-bold text-primary">{value || 0}</p>
        </div>
      </div>
    </div>
  );

  // Helper functions for booking status
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'badge-status-confirmed';
      case 'attended':
        return 'badge-status-attended';
      case 'cancelled':
        return 'badge-status-cancelled';
      case 'no-show':
        return 'badge-status-no-show';
      default:
        return 'badge-status-no-show';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return null;
      case 'attended':
        return null;
      case 'cancelled':
        return <XMarkIcon className="h-4 w-4" />;
      case 'no-show':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const formatClassTime = (startTime) => {
    if (!startTime) return 'Time not available';
    const date = new Date(startTime);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    if (isToday(date)) {
      return `Today at ${date.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow at ${date.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    } else {
      return `${date.toLocaleDateString('en-NZ', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
      })} ${date.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'Duration not available';
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const canCancel = (booking) => {
    if (booking.status !== 'confirmed') return false;
    return true; // Allow cancellation at any time
  };


  return (
    <>
    <div className="space-y-8">
      {/* Welcome Header - Compact Version */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <h1 className="text-xl font-bold">Welcome back, {user?.firstName}!</h1>
              <p className="text-blue-100 text-sm">
                Ready to crush your fitness goals today?
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - one concession card per enrolled purse for marked users */}
      <div className={`grid grid-cols-1 gap-6 ${hasMarks ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`}>
        {hasMarks ? (
          <>
            {userPurses.filter(p => !p.isShared).map((purse) => (
              <StatCard
                key={purse.key}
                title={`${purse.name} Concessions`}
                value={purse.balance}
                icon={BookOpenIcon}
                color="purple"
                alert={purseAlert(purse.balance)}
              />
            ))}
          </>
        ) : (
          <StatCard
            title="Available Concessions"
            value={stats.availableConcessions}
            icon={BookOpenIcon}
            color="purple"
            alert={purseAlert(stats.availableConcessions)}
          />
        )}
        <StatCard
          title="Upcoming Classes"
          value={stats.upcomingClasses}
          icon={CalendarIcon}
          color="orange"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-8">
        {/* Classes & Waitlist - Only show for regular users */}
        {!isAdmin && (
          <div className="bg-card rounded-2xl shadow-sm border border-primary classes-card-mobile">
            {/* Header with Tabs */}
            <div className="p-6 border-b border-primary">
              <div className="classes-card-header-mobile">
                <h2 className="text-xl font-bold text-primary">My Classes & Waitlists</h2>
                <div className="flex bg-tertiary rounded-lg p-1 classes-card-tabs-mobile">
                  <button
                    onClick={() => setActiveTab('upcoming')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'upcoming'
                        ? 'bg-card text-blue-600 shadow-sm'
                        : 'text-secondary hover:text-primary'
                    }`}
                  >
                    Upcoming ({upcomingClasses.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('waitlist')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'waitlist'
                        ? 'bg-card text-blue-600 shadow-sm'
                        : 'text-secondary hover:text-primary'
                    } ${hasAvailableSpotsInWaitlist ? 'tab-notification-dot' : ''}`}
                  >
                    Waitlist ({waitlistClasses.length})
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'upcoming' && (
                <>
                  {upcomingClasses.length > 0 ? (
                    <div className="table-container">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr className="border-b border-primary/20">
                            <th className="table-header table-header-left" style={{width: '20%'}}>Date</th>
                            <th className="table-header table-header-left" style={{width: '25%'}}>Class Name</th>
                            <th className="table-header table-header-left" style={{width: '15%'}}>Duration</th>
                            <th className="table-header table-header-left" style={{width: '20%'}}>Location</th>
                            <th className="table-header table-header-left" style={{width: '15%'}}>Status</th>
                            <th className="table-header table-header-left sticky right-0 bg-card border-l border-primary/10" style={{width: '5%'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingClasses.map((classItem) => (
                            <tr key={classItem._id} className="border-b border-primary/10 hover:bg-primary/5 transition-colors">
                              <td className="table-cell table-cell-left text-xs text-secondary" style={{padding: '8px 12px'}}>
                                {formatClassTime(classItem.displayStartTime || classItem.classId?.startTime)}
                              </td>
                              <td className="table-cell table-cell-left" style={{padding: '8px 12px'}}>
                                <div className="text-sm font-semibold text-primary truncate">{classItem.classId?.name}</div>
                              </td>
                              <td className="table-cell table-cell-left text-xs text-secondary" style={{padding: '8px 12px'}}>
                                <div className="flex items-center">
                                  <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{formatDuration(classItem.classId?.duration)}</span>
                                </div>
                              </td>
                              <td className="table-cell table-cell-left text-xs text-secondary" style={{padding: '8px 12px'}}>
                                <span className="truncate block">{classItem.classId?.location}</span>
                              </td>
                              <td className="table-cell table-cell-left" style={{padding: '8px 12px'}}>
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className={`badge ${getStatusColor(classItem.status)} text-xs mr-1`}>
                                    {getStatusIcon(classItem.status)}
                                    <span>{classItem.status === 'confirmed' ? 'Booked' : classItem.status?.charAt(0)?.toUpperCase() + classItem.status?.slice(1)?.toLowerCase()}</span>
                                  </span>
                                  <span className="badge bg-gray-100 text-gray-800 text-xs mr-1">
                                    <TicketIcon className="h-3 w-3 mr-1" />
                                    {classItem.concessionsUsed || 1}
                                  </span>
                                  {classItem.status === 'confirmed' && canCancel(classItem) && (() => {
                                    const startTime = classItem.displayStartTime || classItem.classId?.startTime;
                                    if (!startTime) return null;
                                    const classStartTime = new Date(startTime);
                                    if (isNaN(classStartTime.getTime())) return null;
                                    const now = new Date();
                                    const hoursUntilClass = (classStartTime - now) / (1000 * 60 * 60);
                                    const isWithin24Hours = hoursUntilClass <= 24;
                                    
                                    return isWithin24Hours ? (
                                      <span className="badge badge-refund-warning text-xs mr-1">
                                        No refund within 24h
                                      </span>
                                    ) : null;
                                  })()}
                                  {classItem.refundedConcessions > 0 && (
                                    <div className="flex items-center text-green-600 text-xs mr-1">
                                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                                      <span>Refunded: {classItem.refundedConcessions}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="table-cell table-cell-left sticky right-0 bg-card border-l border-primary/10" style={{padding: '8px 12px'}}>
                                {classItem.status === 'confirmed' && canCancel(classItem) && (
                                  <button
                                    onClick={() => setConfirmCancel({ 
                                      open: true, 
                                      bookingId: classItem._id, 
                                      className: classItem.classId?.name,
                                      startTime: classItem.displayStartTime || classItem.classId?.startTime
                                    })}
                                    className="btn-danger text-xs px-2 py-1"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CalendarIcon className="w-12 h-12 text-muted mx-auto mb-4" />
                      <p className="text-secondary">No upcoming classes</p>
                      <p className="text-sm text-muted">Book a class to get started!</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'waitlist' && (
                <>
                  {/* Waitlist Description */}
                  <div className="waitlist-banner">
                    <h3>How the Waitlist Works</h3>
                    <p>
                      When a space becomes available in a class, all users on the waitlist will be notified via email. 
                      It's first-come, first-served - the first person to click the link in the email and book the class gets the spot. 
                      Make sure to act quickly when you receive a notification! You can leave the waitlist at any time if you no longer want to be notified.
                    </p>
                  </div>
                  
                  {waitlistClasses.length > 0 ? (
                    <div className="space-y-4">
                      {waitlistClasses.map((waitlistItem) => (
                        <div key={waitlistItem.classId} className="border border-primary rounded-lg p-4 relative">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-primary">{waitlistItem.className}</h3>
                            {waitlistItem.isFull ? (
                              <span className="badge badge-waitlist">
                                On Waitlist
                              </span>
                            ) : (
                              <span className="badge badge-available">
                                {waitlistItem.availableSpots} spots available
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-secondary space-x-4">
                            <div className="flex items-center">
                              <ClockIcon className="w-4 h-4 mr-1" />
                              {formatDate(new Date(waitlistItem.startTime), 'MMM d, h:mm a')}
                            </div>
                            <div className="flex items-center">
                              <MapPinIcon className="w-4 h-4 mr-1" />
                              {waitlistItem.location}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-tertiary">
                            Joined waitlist: {formatDate(new Date(waitlistItem.joinedAt), 'MMM d, h:mm a')}
                          </div>
                          <div className="mt-4 flex justify-between items-center">
                            {waitlistItem.isFull ? (
                              <button
                                onClick={() => setConfirmWaitlist({ open: true, classId: waitlistItem.classId, className: waitlistItem.className })}
                                className="btn-warning"
                              >
                                Leave Waitlist
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => setConfirmWaitlist({ open: true, classId: waitlistItem.classId, className: waitlistItem.className })}
                                  className="text-sm text-orange-600 hover:text-orange-700 underline"
                                >
                                  Leave Waitlist
                                </button>
                                <button
                                  onClick={() => handleOpenClassModal(waitlistItem)}
                                  className="btn-primary"
                                >
                                  Book Now
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <UserGroupIcon className="w-12 h-12 text-muted mx-auto mb-4" />
                      <p className="text-secondary">No waitlist entries</p>
                      <p className="text-sm text-muted">Join a waitlist for a full class to see it here</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div>

            {/* Quick Actions / Concession Payments */}
      <div className="bg-card rounded-2xl shadow-sm border border-primary p-6">
        {isAdmin ? (
          <>
            <h2 className="text-xl font-bold text-primary mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Admin Quick Actions */}
              <button 
                onClick={() => navigate('/admin/users')}
                className="flex flex-col items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200 group cursor-pointer hover:shadow-md"
              >
                <UserIcon className="w-8 h-8 text-blue-600 mb-2 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-blue-600 text-center">Manage Users</span>
                <p className="text-xs text-blue-600 mt-1 text-center">Add, edit, and manage users</p>
              </button>
              
              <button 
                onClick={() => navigate('/admin/classes')}
                className="flex flex-col items-center justify-center p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-all duration-200 group cursor-pointer hover:shadow-md"
              >
                <CalendarIcon className="w-8 h-8 text-green-600 mb-2 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-green-900 text-center">Manage Classes</span>
                <p className="text-xs text-green-600 mt-1 text-center">Create and manage fitness classes</p>
              </button>
              
              <button 
                onClick={() => navigate('/profile')}
                className="flex flex-col items-center justify-center p-4 bg-orange-50 hover:bg-orange-100 rounded-xl transition-all duration-200 group cursor-pointer hover:shadow-md"
              >
                <UserIcon className="w-8 h-8 text-orange-600 mb-2 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-orange-900 text-center">Edit Profile</span>
                <p className="text-xs text-orange-600 mt-1 text-center">Update your information</p>
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-primary mb-6">Concession Payments</h2>
            {/* Bank Account Details - Show admin's bank details for regular users */}
            {adminBankDetails && adminBankDetails.bankName && adminBankDetails.accountNumber ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-tertiary p-3 rounded-lg">
                  <p className="text-sm font-medium text-secondary">Account Name</p>
                  <p className="text-sm text-primary">{adminBankDetails.bankName}</p>
                </div>
                <div className="bg-tertiary p-3 rounded-lg">
                  <p className="text-sm font-medium text-secondary">Account Number</p>
                  <p className="text-sm text-primary">{adminBankDetails.accountNumber}</p>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Bank account details for concession payments are not currently available. 
                  Please contact an administrator to set up payment details.
                </p>
              </div>
            )}
          </>
        )}
        
      </div>
    </div>

    <ConfirmModal
      isOpen={confirmWaitlist.open}
      title="Leave waitlist?"
      message={`Are you sure you want to leave the waitlist for ${confirmWaitlist.className}?`}
      confirmText="Yes, leave"
      cancelText="Stay on waitlist"
      onConfirm={() => {
        if (confirmWaitlist.classId) {
          leaveWaitlistFromDashboard(confirmWaitlist.classId, confirmWaitlist.className);
        }
        setConfirmWaitlist({ open: false, classId: null, className: '' });
      }}
      onCancel={() => setConfirmWaitlist({ open: false, classId: null, className: '' })}
    />

    {/* Confirm Cancel Booking Modal */}
    <ConfirmModal
      isOpen={confirmCancel.open}
      title="Cancel booking?"
      message={`Are you sure you want to cancel your booking? This action cannot be undone.`}
      additionalContent={(() => {
        const classInfo = [];
        
        // Add class name
        if (confirmCancel.className) {
          classInfo.push(
            <div key="class-name" className="mb-2">
              <strong>Class:</strong> {confirmCancel.className}
            </div>
          );
        }
        
        // Add class date and time
        if (confirmCancel.startTime) {
          const classStartTime = new Date(confirmCancel.startTime);
          if (!isNaN(classStartTime.getTime())) {
            classInfo.push(
              <div key="class-time" className="mb-2">
                <strong>Date & Time:</strong> {formatClassTime(confirmCancel.startTime)}
              </div>
            );
          }
        }
        
        // Add refund warning if within 24 hours
        if (confirmCancel.startTime) {
          const classStartTime = new Date(confirmCancel.startTime);
          if (!isNaN(classStartTime.getTime())) {
            const now = new Date();
            const hoursUntilClass = (classStartTime - now) / (1000 * 60 * 60);
            const isWithin24Hours = hoursUntilClass <= 24;
            
            if (isWithin24Hours) {
              classInfo.push(
                <div key="refund-warning" className="mt-3">
                  <span className="badge badge-refund-warning">
                    No refund within 24h
                  </span>
                </div>
              );
            }
          }
        }
        
        return classInfo.length > 0 ? (
          <div className="text-left">
            {classInfo}
          </div>
        ) : null;
      })()}
      confirmText="Yes, cancel"
      cancelText="Keep booking"
      onConfirm={() => {
        if (confirmCancel.bookingId) {
          cancelBooking(confirmCancel.bookingId);
        }
        setConfirmCancel({ open: false, bookingId: null, className: '', startTime: null });
      }}
      onCancel={() => setConfirmCancel({ open: false, bookingId: null, className: '', startTime: null })}
    />

    {/* Class Booking Modal */}
    {showClassModal && selectedClass && (
      <div 
        className="modal-overlay-mobile"
        onClick={handleCloseClassModal}
      >
        <div 
          className="modal-container-mobile"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header-mobile">
            <h3 className="modal-title">Book Class</h3>
          </div>
          
          <div className="modal-body-mobile">
            <div className="form-group">
              {/* Class Information */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-primary">{selectedClass.name}</h4>
                  <p className="text-sm text-secondary mt-1">{selectedClass.description}</p>
                </div>
                
                {/* Instructor */}
                <div className="flex items-center text-sm text-secondary">
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  <span>{selectedClass.instructor}</span>
                </div>

                {/* Time and Duration */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-secondary">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <span>{new Date(selectedClass.startTime).toLocaleDateString()} at {new Date(selectedClass.startTime).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                  </div>
                  <div className="flex items-center text-secondary">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    <span>{selectedClass.duration} minutes</span>
                  </div>
                </div>

                {/* Concessions Required */}
                <div className="flex items-center text-sm text-secondary">
                  <TicketIcon className="h-4 w-4 mr-2" />
                  <span>Concessions required: <span className="font-medium text-primary">{selectedClass.concessionsRequired || 1}</span></span>
                </div>

                {/* Pool balance for this class + Low Concessions Warning - Only show if user is not booked */}
                {user && !selectedClass.isUserBooked && (() => {
                  const isTyped = Boolean(selectedClass.concessionType);
                  const balances = user.concessionBalances || {};
                  const poolBalance = isTyped
                    ? (typeof balances[selectedClass.concessionType] === 'number' ? balances[selectedClass.concessionType] : 0)
                    : (user.concessions || 0);
                  return (
                    <>
                      {isTyped && (
                        <div className="flex items-center text-sm text-secondary">
                          <TicketIcon className="h-4 w-4 mr-2 opacity-0" />
                          <span>
                            Your {poolDisplayName(typeNames, selectedClass.concessionType)} balance:{' '}
                            <span className={`font-medium ${poolBalance < 0 ? 'text-red-500' : 'text-primary'}`}>{poolBalance}</span>
                          </span>
                        </div>
                      )}
                      {poolBalance <= 2 && (
                        <div className="low-concession-modal-warning">
                          <ExclamationTriangleIcon className="low-concession-modal-warning-icon" />
                          <span className="low-concession-modal-warning-text">
                            You only have {poolBalance} concession{poolBalance === 1 ? '' : 's'} left in this pool
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Location */}
                <div className="flex items-center text-sm text-secondary">
                  <MapPinIcon className="h-4 w-4 mr-2" />
                  <span>{selectedClass.location} {selectedClass.room}</span>
                </div>

                {/* Available Spots with Border */}
                <div className="pt-2 border-t border-primary" style={{ marginTop: '0.5rem' }}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary">Available spots:</span>
                    <span className="font-medium text-primary">
                      {selectedClass.availableSpots} / {selectedClass.maxCapacity}
                    </span>
                  </div>
                </div>

                {/* Waitlisted Section - Only show when user is on waitlist */}
                {selectedClass.isUserOnWaitlist && (
                  <div className="pt-2 border-t border-primary" style={{ marginTop: '0.5rem' }}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary">Waitlisted:</span>
                      <button
                        onClick={() => {
                          // Handle leave waitlist
                          setConfirmWaitlist({ open: true, classId: selectedClass._id, className: selectedClass.name });
                          handleCloseClassModal();
                        }}
                        className="leave-waitlist-link"
                      >
                        Leave Waitlist
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="modal-footer-mobile">
            <div className="modal-footer-content">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleCloseClassModal}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                {selectedClass.isUserBooked ? (
                  <button
                    onClick={() => {
                      // Handle cancel booking
                      setConfirmCancel({ open: true, bookingId: selectedClass.userBookingId, className: selectedClass.name, startTime: selectedClass.startTime });
                      handleCloseClassModal();
                    }}
                    className="btn-danger"
                  >
                    Cancel Booking
                  </button>
                ) : selectedClass.isUserOnWaitlist ? (
                  // User is on waitlist - show book now button if class has spots
                  selectedClass.isFull ? (
                    // Class is full, no action needed (leave waitlist is in main content)
                    <div className="text-sm text-secondary text-center">
                      You are on the waitlist
                    </div>
                  ) : (
                    // Class has spots, show book now button
                    <button
                      onClick={async () => {
                        try {
                          await axios.post('/api/bookings/book', {
                            classId: selectedClass._id
                          });
                          toast.success('Successfully booked the class!');
                          handleCloseClassModal();
                          // Refresh user data to update concession balance
                          await refreshUserData();
                          // Refresh dashboard data
                          await fetchDashboardData();
                        } catch (error) {
                          console.error('Booking error:', error);
                          toast.error(error.response?.data?.message || 'Failed to book class');
                        }
                      }}
                      className="btn-primary"
                    >
                      Book Now
                    </button>
                  )
                ) : selectedClass.isFull ? (
                  // User not on waitlist, class is full
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`/api/bookings/waitlist/${selectedClass._id}`);
                        toast.success('Successfully joined the waitlist!');
                        handleCloseClassModal();
                        // Refresh dashboard data
                        await refreshUserData();
                        await fetchDashboardData();
                      } catch (error) {
                        console.error('Waitlist error:', error);
                        toast.error(error.response?.data?.message || 'Failed to join waitlist');
                      }
                    }}
                    className="btn-warning"
                  >
                    Join Waitlist
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post('/api/bookings/book', {
                          classId: selectedClass._id
                        });
                        toast.success('Successfully booked the class!');
                        handleCloseClassModal();
                        // Refresh dashboard data
                        await refreshUserData();
                        await fetchDashboardData();
                      } catch (error) {
                        console.error('Booking error:', error);
                        toast.error(error.response?.data?.message || 'Failed to book class');
                      }
                    }}
                    className="btn-primary"
                  >
                    Confirm Booking
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Dashboard;
